import { useState, useEffect } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { calculateProgress } from "Helpers/Util";

import UseServices from "./UseServices";

/*
 * This hook provides a way to make multiple paginated requests to the back-end
 * when large amounts of data need to be retrieved.
 *
 * It assumes that the back-end will be prepared to read the following query
 * parameters for pagination control:
 *  - page: INTEGER
 *  - page_size: INTEGER
 *  - get_total_rows: BOOLEAN
 *
 * And that it will always return a response with the following structure:
 * { [responseAccessor]: ARRAY, total_rows: INTEGER | NULL, rows_fetched: INTEGER | NULL }
 * If rows_fetched is null, the hook will use the data array's length to calculate
 * how many rows were fetched from the database and calculate next request's offset, i.e.
 * provide the rows_fetched variable when the number of rows fetched from the database
 * is different from the number of items in the response's data array
 *
 * PARAMETERS
 * Options object containing the following properties:
 *  - method: STRING / Required --> HTTP request method e.g. "get", "post", "put"
 *  - path: STRING / Required --> Request path e.g. "/get-my-data"
 *  - body: OBJECT / Optional --> Data to be sent in request body
 *  - queryParams: OBJECT / Optional --> Query parameters to send in request
 *  - responseAccessor: STRING / Required --> Name of the array property in response
 *  containing the data to be saved in local state.
 *  - pageSize: INTEGER / Required --> Number of rows to be retrieved in each request
 *  - cancelRequest: BOOLEAN / Optional --> React Ref that can be used to cancel
 *  further requests by setting its value to "true".
 *
 * RETURN VALUES
 * Object containing the following properties:
 *  - setStartFetch: FUNCTION --> Call with "true" to start the request.
 *  - progress: INTEGER --> Indicates the progress (%) of the request.
 *  - requestInProgress: BOOLEAN --> Tracks if request is in progress.
 *  - firstResponseReceived: BOOLEAN --> Tracks whether the first response was received.
 *  - error: BOOLEAN --> Is set to true when an error occurs during a request.
 *  - data: ARRAY --> Contains the data returned from the requests. Each response adds
 *  to the array until all requests are completed.
 */

export const UsePaginatedFetch = ({
  method,
  path,
  body = {},
  queryParams = {},
  responseAccessor,
  pageSize = 500,
  cancelRequest = {},
}) => {
  const { api } = UseServices();

  const [startFetch, setStartFetch] = useState(false);
  const [progress, setProgress] = useState(0);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [firstResponseReceived, setFirstResponseReceived] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!startFetch) return;

    const getData = async (signal) => {
      setProgress(0);
      setError(false);
      setRequestInProgress(true);
      setFirstResponseReceived(false);
      setData([]);

      cancelRequest.current = false;
      let totalRows = 0;
      let page = 1;
      let getTotalRows = true;
      let lastFetchCount;

      do {
        if (cancelRequest.current) break;

        try {
          const params = new URLSearchParams({
            page,
            page_size: pageSize,
            get_total_rows: getTotalRows,
            ...queryParams,
          });

          let response;

          if (["post", "put", "patch"].includes(method.toLowerCase())) {
            response = await api[method](`${path}?${params}`, body, { signal });
          } else {
            response = await api[method](`${path}?${params}`, { signal });
          }

          lastFetchCount =
            response.data.rows_fetched ??
            response.data[responseAccessor].length;

          if (getTotalRows) {
            totalRows = response.data.total_rows;
            getTotalRows = false;
          }

          page += 1;

          setData((d) => [...d, ...response.data[responseAccessor]]);

          setProgress(calculateProgress(totalRows, pageSize, page));

          setFirstResponseReceived(true);
        } catch (error) {
          if (error.name === "CanceledError") return;
          setError(true);
          HTTPErrorCallback(error);
          break;
        }
      } while (lastFetchCount >= pageSize);

      setStartFetch(false);
      setRequestInProgress(false);
    };

    const controller = new AbortController();
    const signal = controller.signal;

    getData(signal);

    return () => controller.abort();
    // eslint-disable-next-line
  }, [startFetch]);

  return {
    setStartFetch,
    progress,
    requestInProgress,
    firstResponseReceived,
    error,
    data,
  };
};
